import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { purchaseRequestsApi } from '../purchaseRequestsApi';

/**
 * Cancels a request, with the reason.
 *
 * **A modal because the reason is required**, and because cancelling is the state most easily
 * confused with deleting — the body says which this is before the tick.
 *
 * <b>THE WORD IS THE SOURCE'S.</b> Its register tabs read `Cancelled`, and the image the
 * capture was given is the specification; this said "Withdraw" for an hour, which was invented
 * here and matched neither the image nor `PurchaseRequestStatus.Cancelled`.
 */
export function CancelPurchaseRequestModal({
  open,
  request,
  isSubmitted = false,
  onClose,
  onCancelled,
}: {
  open: boolean;
  request: { id: string; requestNumber: string };
  /** Whether an approval is pending on it, so the dialog can say it will be closed. */
  isSubmitted?: boolean;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = reason.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await purchaseRequestsApi.cancel(request.id, reason.trim());
      onCancelled();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not cancel this request.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'cancel-purchase-request' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cancel ${request.requestNumber}`}
      size="lg"
      id="cancel-purchase-request-modal"
      /*
        NO Autogenerate. Why the organization decided not to buy something is a judgement
        somebody is making about a specific request — generating one would put words in their
        mouth, which is the one place the generator would be actively wrong.
      */
      confirm={{
        id: 'cancel-purchase-request-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="cancel-purchase-request-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="cancel-purchase-request-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          CANCELLING IS NOT DELETING, and the two are the most confusable states in this
          module. Cancelled is an OUTCOME the register keeps; a delete is a correction to the
          file. Stated before the tick, and pointing a reader who reached for the wrong one at
          the other.
        */}
        <div className="flex flex-col gap-2 text-xs text-slate-600">
          <p>
            <strong>Cancelled is an outcome the register keeps.</strong> The request stays
            visible, marked cancelled, with this reason against it — which is what a reader
            looking back at the year wants to find.
          </p>
          <p>
            If it was keyed against the wrong record, or twice, <strong>Delete</strong> is the
            one you want instead.
          </p>
          {isSubmitted ? (
            <p>Any approval still pending on it is closed in the same action.</p>
          ) : null}
        </div>

        <Textarea
          id="cancel-purchase-request-reason"
          label="Reason"
          required
          description="Why the organization decided not to buy this. It stays on the record."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reason.trim() ? error?.fieldError('reason') : 'Say why it is being cancelled.'}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
